import { useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useRecordContext,
} from "ra-core";
import { Button } from "@/components/ui/button";

import {
  STUDIO_EMAIL,
  STUDIO_FROM,
} from "../../../../supabase/functions/_shared/vogelkop.ts";
import type {
  Contact,
  FollowUpEnrollment,
  FollowUpSequence,
  FollowUpStep,
  Message,
} from "../types";
import { sequenceSkipReason, startFollowUp, stopFollowUps } from "./followUps";

const stepLabel = (message: Message | undefined) => {
  if (!message) return "Not scheduled";
  if (message.status === "cancelled") return "Cancelled";
  if (message.status === "scheduled" && message.scheduled_at) {
    return `Scheduled for ${new Date(message.scheduled_at).toLocaleString()}`;
  }
  if (
    message.sent_at ||
    message.status === "delivered" ||
    message.status === "sent"
  ) {
    return "Sent";
  }
  return message.status;
};

export const FollowUpSequencePanel = () => {
  const contact = useRecordContext<Contact>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [busy, setBusy] = useState(false);
  const { data: sequences = [], isPending: sequencesPending } =
    useGetList<FollowUpSequence>("follow_up_sequences", {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "name", order: "ASC" },
    });
  const { data: steps = [] } = useGetList<FollowUpStep>("follow_up_steps", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "position", order: "ASC" },
  });
  const { data: enrollments = [], refetch: refetchEnrollments } =
    useGetList<FollowUpEnrollment>(
      "follow_up_enrollments",
      {
        filter: contact ? { contact_id: contact.id } : {},
        pagination: { page: 1, perPage: 20 },
        sort: { field: "started_at", order: "DESC" },
      },
      { enabled: Boolean(contact) },
    );
  const { data: messages = [], refetch: refetchMessages } = useGetList<Message>(
    "messages",
    {
      filter: contact ? { contact_id: contact.id } : {},
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
    { enabled: Boolean(contact) },
  );
  if (!contact || sequencesPending) return null;

  const active = enrollments.find((item) => item.status === "active");
  const shown = active ?? enrollments[0];
  const sequence = sequences.find((item) => item.id === shown?.sequence_id);
  const sequenceSteps = steps
    .filter(
      (step) => step.sequence_id === (shown?.sequence_id ?? sequences[0]?.id),
    )
    .sort((a, b) => a.position - b.position);
  const blocked = sequenceSkipReason(contact);

  const refresh = () => {
    void refetchEnrollments();
    void refetchMessages();
  };

  const start = async (sequenceId: FollowUpSequence["id"]) => {
    if (blocked || active) return;
    setBusy(true);
    try {
      await startFollowUp(dataProvider, {
        contact,
        sequenceId,
        steps: steps.filter((step) => step.sequence_id === sequenceId),
        salesId: identity?.id,
        repName: identity?.fullName ?? "Vogelkop",
      });
      notify("Follow-up sequence started");
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not start", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const replied = async () => {
    if (!active) return;
    const email = contact.email_jsonb?.find((item) => item.email)?.email;
    setBusy(true);
    try {
      await dataProvider.create("messages", {
        data: {
          contact_id: contact.id,
          sales_id: identity?.id ?? null,
          channel: "email",
          direction: "inbound",
          status: "received",
          subject: "Reply",
          body: "They replied.",
          from_address: email ?? null,
          to_address: STUDIO_EMAIL,
          provider: "intake",
          created_at: new Date().toISOString(),
        },
      });
      await stopFollowUps(dataProvider, contact.id, "They replied");
      notify("Sequence stopped");
      refresh();
    } catch {
      notify("Could not record the reply", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Follow-up sequence"
      className="mb-6 rounded-xl border bg-muted/30 p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            {sequence?.name ?? "Follow-up sequence"}
          </h2>
          <p className="text-sm text-muted-foreground">
            Email only, from {STUDIO_FROM}. It stops when they reply or the
            stage changes.
          </p>
          {shown?.status === "stopped" ? (
            <p className="mt-1 text-sm">Stopped: {shown.stop_reason}</p>
          ) : null}
        </div>
        {active ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void replied()}
          >
            They replied
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            disabled={busy || Boolean(blocked) || sequences.length === 0}
            onClick={() => void start(sequences[0].id)}
          >
            Start sequence
          </Button>
        )}
      </div>
      {blocked && !active ? (
        <p className="mt-2 text-sm text-muted-foreground">{blocked}</p>
      ) : null}
      <ol className="mt-3 space-y-2">
        {sequenceSteps.map((step) => {
          const message = messages.find(
            (item) =>
              item.step_id === step.id &&
              (shown ? item.enrollment_id === shown.id : false),
          );
          return (
            <li key={step.id} className="text-sm">
              <p className="font-medium">
                Day {step.day_offset}
                {step.subject ? ` · ${message?.subject ?? step.subject}` : ""}
              </p>
              <p className="text-muted-foreground">{stepLabel(message)}</p>
            </li>
          );
        })}
      </ol>
    </section>
  );
};
